import { ItemType } from '../../_types/item.ts';
import { handleSignOnRedirect } from '../../../../../_helpers/handleSignOnRedirect';
import { dispatchSavedFetchedEvent } from './savedChangeEvent';
import {
	NO_COOKIES_ACCEPTED_ERROR,
	NO_COOKIES_ACCEPTED_ERROR_MSG,
	NO_COOKIES_ACCEPTED_STATUS_CODE,
} from '../../../../../_helpers/manageCookies';
import { SavableSeriesItemData } from '../../../../../cms-components/blackrock/advisor-center/saved-page/SavedItemsReducer.ts';

export type SaveItem = {
	data: string;
	itemType: ItemType;
};

export type SavedResponse = SaveItem & {
	id: string;
	dateOfSave: number;
};

export const getSaveItem = (item: SavableSeriesItemData): SaveItem => ({
	data: item.saveUrl,
	itemType: item.itemType,
});

// Fetch saved state of SeriesItem and dispatch saved state fetched event
export const fetchSavedStateThenDispatch = (
	saveFetchUrl: string,
	item: SavableSeriesItemData,
	newSavedState: boolean,
	componentName: string,
	componentId?: string
): Promise<SavableSeriesItemData> =>
	fetchSavedStatePromise(
		saveFetchUrl,
		getSaveItem(item),
		newSavedState,
		componentName,
		item.dateOfSave
	).then((resp: SavedResponse | null) => {
		const newItem = {
			...item,
			saved: newSavedState,
			dateOfSave: resp?.dateOfSave,
		};
		dispatchSavedFetchedEvent(
			item.saveUrl,
			newSavedState,
			componentId,
			newItem
		);
		return newItem;
	});

export const fetchSavedStatePromise = (
	saveFetchUrl: string,
	saveItem: SaveItem,
	newSavedState: boolean,
	componentName: string,
	dateOfSave?: number | null // in case of undo
): Promise<SavedResponse | null> =>
	fetch(saveFetchUrl + getParams(newSavedState, componentName, dateOfSave), {
		method: newSavedState ? 'POST' : 'DELETE',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(saveItem),
	}).then((response: Response) => {
		// If no user is logged in, the server sends a response with 401 (Unauthorized) status code.
		// In this case the 'Signonurl' header in the response contains the URL of the login page.
		if (handleSignOnRedirect(response)) {
			return;
		}

		// If the user not accepted the necessary cookies, the server sends a response with 451 (Unavailable For Legal Reasons) status code.
		// This is a double check, because we also check it on frontend side.
		if (response.status === NO_COOKIES_ACCEPTED_STATUS_CODE) {
			const error = new Error(NO_COOKIES_ACCEPTED_ERROR_MSG);
			error.name = NO_COOKIES_ACCEPTED_ERROR;
			throw error;
		}

		if (!response.ok) {
			throw new Error(`Error, response status: ${response.status}`);
		}

		// In case of save the response is a SavedResponse object,
		// In case of unsave there is no response object.
		if (newSavedState) {
			return response.json();
		} else {
			return null;
		}
	});

const getParams = (
	newSavedState: boolean,
	componentName: string,
	dateOfSave?: number | null
): string => {
	if (!newSavedState) return '';

	const params: Record<string, string> = {
		componentName: componentName,
	};

	if (dateOfSave) {
		params.dateOfSave = dateOfSave.toString();
	}

	return '?' + new URLSearchParams(params).toString();
};
